import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Input,
  useDisclosure,
  Heading,
  IconButton,
  HStack,
  useToast,
  Spinner,
  Center,
  Switch,
  Flex,
  Text,
  Badge,
} from '@chakra-ui/react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/table';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/modal';
import {
  FormControl,
  FormLabel,
} from '@chakra-ui/form-control';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const Wordpress = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    host: '',
    username: '',
    password: '',
    webhook_secret: '',
    verify_ssl: true,
    is_active: true,
  });
  const toast = useToast();

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/wordpress/', {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setConfigs(data);
      } else {
        toast({
          title: 'Error fetching configurations',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error fetching configurations',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleEdit = (config) => {
    setSelectedConfig(config);
    setFormData({
      name: config.name || '',
      host: config.host || '',
      username: config.username || '',
      password: '',
      webhook_secret: config.webhook_secret || '',
      verify_ssl: config.verify_ssl !== false,
      is_active: config.is_active !== false,
    });
    onOpen();
  };

  const handleAdd = () => {
    setSelectedConfig(null);
    setFormData({
      name: '',
      host: '',
      username: '',
      password: '',
      webhook_secret: '',
      verify_ssl: true,
      is_active: true,
    });
    onOpen();
  };

  const handleDelete = async (config) => {
    if (!window.confirm(`Are you sure you want to delete "${config.name}"?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      const response = await fetch(`/api/v1/wordpress/${config.id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'X-CSRFToken': csrftoken,
        },
      });
      if (response.ok) {
        toast({
          title: 'Configuration deleted',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchConfigs();
      } else {
        toast({
          title: 'Error deleting configuration',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error deleting configuration',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      const url = selectedConfig
        ? `/api/v1/wordpress/${selectedConfig.id}/`
        : '/api/v1/wordpress/';
      const method = selectedConfig ? 'PUT' : 'POST';

      const dataToSend = { ...formData };
      if (selectedConfig && !dataToSend.password) {
        delete dataToSend.password;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        toast({
          title: `Configuration ${selectedConfig ? 'updated' : 'created'}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onClose();
        setSelectedConfig(null);
        fetchConfigs();
      } else {
        const errorData = await response.json();
        toast({
          title: 'Error saving configuration',
          description: JSON.stringify(errorData),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error saving configuration',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  if (loading) {
    return (
      <Center h="200px">
        <Spinner size="xl" color="purple.500" thickness="4px" />
      </Center>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="gray.800">WordPress Configurations</Heading>
          <Text color="gray.500" mt={1}>Manage your WordPress site connections</Text>
        </Box>
        <Button
          leftIcon={<FaPlus />}
          bgGradient="linear(to-r, purple.500, purple.600)"
          color="white"
          _hover={{
            bgGradient: "linear(to-r, purple.600, purple.700)",
            transform: "translateY(-2px)",
            boxShadow: "lg",
          }}
          onClick={handleAdd}
          borderRadius="lg"
          px={6}
        >
          Add Configuration
        </Button>
      </Flex>

      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        borderWidth="1px"
        borderColor="gray.100"
        overflow="hidden"
      >
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th color="gray.600" py={4}>Name</Th>
              <Th color="gray.600">Host</Th>
              <Th color="gray.600">Username</Th>
              <Th color="gray.600">Status</Th>
              <Th color="gray.600" textAlign="right">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {configs.length === 0 ? (
              <Tr>
                <Td colSpan={5} textAlign="center" py={8} color="gray.500">
                  No configurations found. Click "Add Configuration" to create one.
                </Td>
              </Tr>
            ) : (
              configs.map((config) => (
                <Tr
                  key={config.id}
                  _hover={{ bg: "gray.50" }}
                  transition="background 0.2s"
                >
                  <Td fontWeight="medium" color="gray.800">{config.name}</Td>
                  <Td color="gray.600" maxW="250px" isTruncated>{config.host}</Td>
                  <Td color="gray.600">{config.username}</Td>
                  <Td>
                    <Badge
                      colorScheme={config.is_active ? 'green' : 'gray'}
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      {config.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td textAlign="right">
                    <HStack spacing={2} justify="flex-end">
                      <IconButton
                        aria-label="Edit configuration"
                        icon={<FaEdit />}
                        size="sm"
                        variant="ghost"
                        colorScheme="purple"
                        onClick={() => handleEdit(config)}
                      />
                      <IconButton
                        aria-label="Delete configuration"
                        icon={<FaTrash />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDelete(config)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>
            {selectedConfig ? 'Edit' : 'Add New'} Configuration
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <FormControl isRequired>
              <FormLabel fontWeight="medium">Name</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Production"
                borderRadius="lg"
                focusBorderColor="purple.500"
              />
            </FormControl>
            <FormControl mt={4} isRequired>
              <FormLabel fontWeight="medium">Host</FormLabel>
              <Input
                name="host"
                value={formData.host}
                onChange={handleInputChange}
                placeholder="https://example.com"
                borderRadius="lg"
                focusBorderColor="purple.500"
              />
            </FormControl>
            <FormControl mt={4} isRequired>
              <FormLabel fontWeight="medium">Username</FormLabel>
              <Input
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                borderRadius="lg"
                focusBorderColor="purple.500"
              />
            </FormControl>
            <FormControl mt={4} isRequired={!selectedConfig}>
              <FormLabel fontWeight="medium">
                Password {selectedConfig && <Text as="span" color="gray.500" fontWeight="normal">(leave blank to keep current)</Text>}
              </FormLabel>
              <Input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                borderRadius="lg"
                focusBorderColor="purple.500"
              />
            </FormControl>
            <FormControl mt={4} isRequired>
              <FormLabel fontWeight="medium">Webhook Secret</FormLabel>
              <Input
                name="webhook_secret"
                value={formData.webhook_secret}
                onChange={handleInputChange}
                borderRadius="lg"
                focusBorderColor="purple.500"
              />
            </FormControl>
            <Flex mt={6} gap={8}>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" fontWeight="medium">Verify SSL</FormLabel>
                <Switch
                  name="verify_ssl"
                  isChecked={formData.verify_ssl}
                  onChange={handleInputChange}
                  colorScheme="purple"
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" fontWeight="medium">Active</FormLabel>
                <Switch
                  name="is_active"
                  isChecked={formData.is_active}
                  onChange={handleInputChange}
                  colorScheme="purple"
                />
              </FormControl>
            </Flex>
          </ModalBody>

          <ModalFooter borderTopWidth="1px" py={4}>
            <Button variant="ghost" mr={3} onClick={onClose} borderRadius="lg">
              Cancel
            </Button>
            <Button
              bgGradient="linear(to-r, purple.500, purple.600)"
              color="white"
              _hover={{
                bgGradient: "linear(to-r, purple.600, purple.700)",
              }}
              onClick={handleSave}
              borderRadius="lg"
              px={6}
            >
              {selectedConfig ? 'Update' : 'Create'} Configuration
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Wordpress;
